import {FC, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Icon, Modal} from "semantic-ui-react";
import {LoadingMessage, ErrorMessage} from "@/components/SystemMessages";
import {DataStatus} from "@/features/application/api/applicationModels";
import {AppState} from "@/state/rootReducer";
import {ISODateString, formatDateString} from "@/util/dateUtil";
import driveThruReplayAPI from "@/features/coachInsights/driveThruReplay/api/driveThruReplayAPI";
import {mapReplayDataToEvents} from "@/features/coachInsights/driveThruReplay/api/driveThruReplayMapper";
import {IMappedReplayData} from "@/features/coachInsights/driveThruReplay/api/driveThruReplayMapper";
import {
    formatDuration,
    formatHour
} from "@/features/coachInsights/api/coachInsightsUtils";
import {getConceptDriveThruThresholdsConfig} from "../api/coachInsightsConceptConfig";
import DriveThruTimeline from "@/features/coachInsights/driveThruReplay/components/DriveThruTimeline";
import "@/scss/drive-thru-replay.scss";
import DriveThruTransactionPanel from "@/features/coachInsights/driveThruReplay/components/DriveThruTransactionPanel";
import DriveThruCascadeContext from "@/features/coachInsights/driveThruReplay/components/DriveThruCascadeContext";
import DriveThruVideoPlayer from "@/features/coachInsights/driveThruReplay/components/DriveThruVideoPlayer";

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    locationPk: number;
    locationName: string;
    businessDate: ISODateString;
    timeframeStartHour: number;
    timeframeEndHour: number;
}

const DriveThruReplayModal: FC<IProps> = ({
    isOpen,
    onClose,
    locationPk,
    locationName,
    businessDate,
    timeframeStartHour,
    timeframeEndHour
}) => {
    const concept = useSelector(
        (state: AppState) => state.application.selectedClientConcept.concept
    );
    const conceptConfig = useMemo(
        () => getConceptDriveThruThresholdsConfig(concept),
        [concept]
    );

    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.Undefined);
    const [mappedData, setMappedData] = useState<IMappedReplayData | null>(null);
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!isOpen) {
            // Reset eagerly on close so the next open never shows stale content.
            abortControllerRef.current?.abort();
            setDataStatus(DataStatus.Undefined);
            setMappedData(null);
            setSelectedEventId(null);
            return;
        }

        setDataStatus(DataStatus.Loading);

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const load = async () => {
            try {
                const replayResponse = await driveThruReplayAPI.getDriveThruDayData(
                    locationPk,
                    businessDate,
                    controller.signal
                );

                if (!replayResponse) {
                    setDataStatus(DataStatus.Failed);
                    return;
                }

                const mapped = mapReplayDataToEvents(replayResponse, conceptConfig);
                setMappedData(mapped);

                let initialEventId: string | null = null;
                for (const event of mapped.events) {
                    if (mapped.cascadeMap.has(event.id)) {
                        initialEventId = event.id;
                        break;
                    }
                }
                setSelectedEventId(initialEventId);

                setDataStatus(DataStatus.Loaded);
            } catch (error: unknown) {
                if (error instanceof Error && error.name === "AbortError") return;
                setDataStatus(DataStatus.Failed);
            }
        };

        load();

        return () => controller.abort();
    }, [isOpen, locationPk, businessDate, conceptConfig]);

    const selectedEvent =
        mappedData && selectedEventId
            ? mappedData.events.find((e) => e.id === selectedEventId) ?? null
            : null;

    const selectedTransaction =
        selectedEvent && mappedData
            ? mappedData.transactions[selectedEvent.transactionId] ?? null
            : null;

    const cascadeSiblings =
        selectedEvent && mappedData
            ? (() => {
                  const cascade = mappedData.cascadeMap.get(selectedEvent.id);
                  if (!cascade) return [];
                  return mappedData.events
                      .filter(
                          (e) =>
                              e.id !== selectedEventId &&
                              mappedData.cascadeMap.get(e.id)?.groupId ===
                                  cascade.groupId
                      )
                      .map((e) => ({
                          event: e,
                          transaction:
                              mappedData.transactions[e.transactionId] ?? null,
                          cascadeInfo: mappedData.cascadeMap.get(e.id)!
                      }));
              })()
            : [];

    const totalCars = mappedData?.events.length ?? 0;
    const overThreshold =
        mappedData?.events.filter((e) => e.isOverThreshold).length ?? 0;
    const avgSeconds =
        totalCars > 0
            ? Math.round(
                  mappedData!.events.reduce((s, e) => s + e.totalServiceSeconds, 0) /
                      totalCars
              )
            : 0;

    return (
        <Modal
            className="drive-thru-replay-modal"
            open={isOpen}
            onClose={onClose}
            closeIcon
            size="fullscreen"
        >
            <Modal.Header>
                <div className="drive-thru-replay-modal-header-content">
                    <div className="drive-thru-replay-modal-title">
                        <Icon name="play circle" />
                        Drive-Thru Replay — {locationName}
                    </div>
                    <div className="drive-thru-replay-modal-subtitle">
                        <span>{formatDateString(businessDate)}</span>
                        <span className="header-separator">&bull;</span>
                        <span className="drive-thru-replay-peak-badge">
                            Occurred: {formatHour(timeframeStartHour)}-
                            {formatHour(timeframeEndHour)}
                        </span>
                        {dataStatus === DataStatus.Loaded && (
                            <>
                                <span className="header-separator">&bull;</span>
                                <span>{totalCars} cars</span>
                                <span className="header-separator">&bull;</span>
                                <span
                                    className={
                                        overThreshold > 0
                                            ? "drive-thru-replay-over-threshold-stat"
                                            : ""
                                    }
                                >
                                    {overThreshold} over threshold
                                </span>
                                <span className="header-separator">&bull;</span>
                                <span>Avg {formatDuration(avgSeconds)}</span>
                            </>
                        )}
                    </div>
                </div>
            </Modal.Header>
            <Modal.Content scrolling>
                {dataStatus === DataStatus.Loading && <LoadingMessage />}
                {dataStatus === DataStatus.Failed && (
                    <ErrorMessage
                        text="Failed to load Drive-Thru Replay data."
                        inline
                    />
                )}
                {dataStatus === DataStatus.Loaded && mappedData && (
                    <div className="drive-thru-replay-modal-layout drive-thru-replay">
                        <DriveThruTimeline
                            events={mappedData.events}
                            selectedEventId={selectedEventId}
                            onSelectEvent={setSelectedEventId}
                            thresholdSeconds={mappedData.thresholdSeconds}
                            transactions={mappedData.transactions}
                            cascadeMap={mappedData.cascadeMap}
                            peakWindowHours={{
                                start: timeframeStartHour,
                                end: timeframeEndHour
                            }}
                        />
                        <div className="drive-thru-replay-main-panel">
                            <DriveThruVideoPlayer
                                locationPk={locationPk}
                                businessDate={businessDate}
                                selectedSecondOfDay={
                                    selectedEvent?.secondOfDay ?? null
                                }
                                selectedTotalSec={
                                    selectedEvent?.totalServiceSeconds ?? null
                                }
                            />
                            <div className="drive-thru-replay-detail-section">
                                <DriveThruTransactionPanel
                                    event={selectedEvent}
                                    transaction={selectedTransaction}
                                    conceptConfig={conceptConfig}
                                />
                                <DriveThruCascadeContext
                                    siblings={cascadeSiblings}
                                    onSelectEvent={setSelectedEventId}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Content>
        </Modal>
    );
};

export default DriveThruReplayModal;
